import type { CarlineGroup } from '../../@types/mapped';
import type { CompareTrackOptions } from '../hooks/useCompareTrackingManager';
import type { CompareCarline, State } from './CompareContext';

export type AddAction = {
  readonly type: 'add';
  readonly carline: CompareCarline;
};

export type RemoveAction = {
  readonly type: 'remove';
  readonly carline: CompareCarline;
};

export type ClearAction = {
  readonly type: 'clear';
};

export type InitAction = {
  readonly type: 'init';
  readonly carlines: string[];
  readonly allCarlineGroups: CarlineGroup[];
};

export type SetCarsAction = {
  readonly type: 'set-cars';
  readonly amount: 2 | 3;
  readonly trackAutoRemove: (options: CompareTrackOptions) => void;
};

export type Action = AddAction | RemoveAction | ClearAction | InitAction | SetCarsAction;

const add = (state: State, { carline }: AddAction): State => {
  if (state.carlines.length >= state.amount || state.carlines.indexOf(carline) !== -1) {
    return state;
  }

  return {
    ...state,
    carlines: [...state.carlines, carline],
  };
};

const remove = (state: State, { carline }: RemoveAction): State => {
  const index = state.carlines.findIndex(({ id }) => id === carline.id);
  if (index === -1) {
    return state;
  }

  return {
    ...state,
    carlines: state.carlines.filter(({ id }) => id !== carline.id),
  };
};

const clear = (state: State): State => {
  if (state.carlines.every((carline) => carline === undefined)) {
    return state;
  }
  return { ...state, carlines: [] };
};

const init = (state: State, action: InitAction): State => {
  const ngcCarlines = action.allCarlineGroups.flatMap<CompareCarline>(({ name, carlines }) =>
    carlines.map((carline) => ({ id: carline.identifier.id, name: carline.name, group: name })),
  );
  return {
    ...state,
    carlines: action.carlines
      .slice(0, state.amount)
      .map((id) => ngcCarlines.find((cl) => id === cl.id))
      .filter((cl) => cl !== undefined),
  };
};

const setCars = (state: State, { amount, trackAutoRemove }: SetCarsAction): State => {
  if (state.amount !== amount) {
    if (amount < state.carlines.length) {
      trackAutoRemove({
        carline: state.carlines[state.carlines.length - 1],
      });
    }
    return { ...state, amount, carlines: state.carlines.slice(0, amount) };
  }
  return state;
};

export const getNumberOfCars = (mobile: boolean): 2 | 3 => (mobile ? 2 : 3);

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'add':
      return add(state, action);
    case 'remove':
      return remove(state, action);
    case 'clear':
      return clear(state);
    case 'init':
      return init(state, action);
    case 'set-cars':
      return setCars(state, action);
    default:
      return state;
  }
};
